import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable } from "mobx";

import {JsonSerializer, throwError} from "typescript-json-serializer";
import {ApiLayerConfiguration} from "../config/api-configuration";
import {Steckbrief, SteckbriefProps} from "../models/steckbrief";
import {ItemType} from "../models/item-type";

export interface SteckbriefStoreProps {
    layerConfig: ApiLayerConfiguration;
}

export class SteckbriefStore extends AbstractStore {

    /**
     * Loaded items (local store)
     * Key: fileName
     */
    items = observable.map<string, Steckbrief>();

    constructor(props: SteckbriefStoreProps) {
        super({ layerConfig: props.layerConfig });

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            filters: observable,
            sort: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): Steckbrief[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        filtered.sort(Steckbrief.sortByNameAndRiskType);
        return [ ...filtered];
    }

    resetVisibleItems() {
        this.items.clear();
    }

    /**
     * Override to use bfsNr as key
     * Add one or multiple items to the store
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: Steckbrief | Map<string, Steckbrief>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(item.fileName)) {
                this.items.delete(item.fileName);
            }
            this.items.set(item.fileName, item);
        }
    }

    /**
     * Override to use bfsNr as key
     * Remove an item from store
     * @param item
     */
    removeItem(item: Steckbrief): void {
        if (this.items.has(item.fileName)) {
            this.items.delete(item.fileName);
        }
    }

    /**
     * Override to use bfsNr as key
     * Get an item by fileName
     * @param fileName
     */
    getItem(fileName: string): Steckbrief | null {
        const item = this.items.get(fileName);
        return item || null;
    }

    loadItem(fileName: string | undefined): Promise<Steckbrief | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!fileName) {
                    reject("No file name provided.");
                    return;
                }

                this.querySteckbriefFeatures([fileName]).then((steckbriefe: Steckbrief[]) => {
                    // BFS number is also stored in guid property
                    if (steckbriefe[0] && steckbriefe[0].guid === fileName) {
                        const steckbrief = steckbriefe[0];
                        const loadedSteckbrief = this.items.get(String(fileName));
                        if (loadedSteckbrief) {
                            // return municipality as long as merging municipality is running.
                            // municipality can be displayed with
                            // indicator that synching is in municipality
                            // TODO: merge municipality with already loaded activity
                        }

                        this.addItem(steckbrief);
                        this.setSelectedItem(steckbrief);
                        this.setDataStatus("loaded");

                        resolve(steckbrief);
                    } else {
                        this.setDataStatus("error");
                        reject("Steckbrief not found.");
                        throw new Error("Steckbrief not found.");
                    }
                });
            });
        });
    }

    /**
     * Request municipalities from server and store in local store
     * @param bfsNrs (Optional) BFS numbers of municipalities
     * @param processSources (Optional) Process sources
     */
    loadItems(bfsNrs?: string[], processSources?: string[]): Promise<Steckbrief[]> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.querySteckbriefFeatures(bfsNrs, processSources).then((steckbriefe: Steckbrief[]) => {
                    if (steckbriefe) {
                        this.addSteckbriefe(steckbriefe);
                        this.setDataStatus("loaded");

                        resolve(steckbriefe);
                    } else {
                        this.setDataStatus("error");
                        reject("Error requesting steckbriefe.");
                        throw new Error("Error requesting steckbriefe.");
                    }
                });
            });
        });
    }

    addSteckbriefe(steckbriefe: Steckbrief[] | [] | null): void {
        // do only save to mobx when not requested with specified ids or steckbriefe
        const map = new Map<string, Steckbrief>();
        steckbriefe?.forEach((m: Steckbrief) => {
            map.set(String(m.fileName), m);
        });
        this.addItem(map);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    queryFeatures(ids?: string[], unique?: true): Promise<ItemType[] | [] | null> {
        throw new Error("Use specific query function of this store.");
    }

    /**
     * Query steckbrief features from feature layer
     * @param bfsNrs Gemeindenummern
     * @param processSources Prozessquellen
     */
    querySteckbriefFeatures(bfsNrs?: string[], processSources?: string[]): Promise<Steckbrief[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                const queryParams = this.layer.createQuery();

                if (bfsNrs !== undefined) {
                    queryParams.where += ` AND (${this.multiCompareWhereClause("BFS_NUMMERN", bfsNrs)})`;
                }
                if (processSources !== undefined) {
                    queryParams.where += ` AND (${this.multiCompareWhereClause("PROZESSQUELLEN", processSources)})`;
                }

                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const steckbriefe: Steckbrief[] = this.parseQueryResult(results) as Steckbrief[];
                        resolve(steckbriefe);
                    } else {
                        reject("Error querying steckbriefe.");
                    }
                }, (error) => {
                    throw new Error(`Error querying steckbriefe + ${error}`);
                });
            });
        });
    }

    private multiCompareWhereClause(fieldName: string, bfsNrs: string[] | number[]) {
        const whereClauses = bfsNrs.map((bfsNr) => `${fieldName} LIKE '%${bfsNr}%'`);
        return whereClauses.join(" OR ");
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    zoomToItem(guid: string): void {
        throw new Error("Method not implemented.");
    }

    private parseQueryResult(data: __esri.FeatureSet): Steckbrief[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const steckbriefe: Steckbrief[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, Steckbrief);
            const m = new Steckbrief(json as SteckbriefProps);
            m.feature = feature;
            steckbriefe.push(m);
        });
        return steckbriefe;
    }

}
